/**
 * <brand-load-more> — the "Încarcă mai multe" button under the catalog grid
 * (sections/main-collection.liquid, Figma 637:5061 / 817:30511).
 *
 * Clicking it fetches the next page through the theme's section-rendering endpoint, appends the
 * new cards to the live grid and moves the progress bar. The URL is left alone: the grid holds
 * every page loaded so far, so a `?page=` in the address bar would no longer describe it.
 *
 * Markup contract (see snippets/brand-load-more.liquid):
 *   section-id, data-page, data-last-page, data-total, data-shown
 *   data-label / data-progress-label / data-range-label — templates with [remaining], [shown],
 *   [total], [from] and [to] placeholders.
 */
import { sectionRenderer } from '@theme/section-renderer';

class BrandLoadMore extends HTMLElement {
  /** @type {boolean} */
  #busy = false;

  connectedCallback() {
    this.button = this.querySelector('[data-load-more-button]');
    this.label = this.querySelector('[data-load-more-label]');
    this.status = this.querySelector('[data-load-more-status]');

    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  get #page() {
    return Number(this.dataset.page || 1);
  }

  get #lastPage() {
    return Number(this.dataset.lastPage || 1);
  }

  get #total() {
    return Number(this.dataset.total || 0);
  }

  get #shown() {
    return Number(this.dataset.shown || 0);
  }

  /** The grid this button feeds. It lives in the same section, not inside this element. */
  get #grid() {
    return this.closest('results-list')?.querySelector('.product-grid') ?? null;
  }

  #onClick = async () => {
    if (this.#busy || this.#page >= this.#lastPage) return;

    const grid = this.#grid;
    if (!grid) return;

    this.#setBusy(true);

    const next = this.#page + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(next));
    url.hash = '';

    try {
      const html = await sectionRenderer.getSectionHTML(this.getAttribute('section-id') ?? '', true, url);
      const cards = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.product-grid__item');

      if (!cards.length) {
        this.#setBusy(false);
        return;
      }

      const first = cards[0];
      grid.append(...cards);

      this.dataset.page = String(next);
      this.dataset.shown = String(Math.min(this.#shown + cards.length, this.#total));
      this.#render();

      // Send focus to the first new card so keyboard users do not lose their place.
      first?.querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      console.error('brand-load-more: could not load the next page', error);
    } finally {
      this.#setBusy(false);
    }
  };

  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('data-busy', busy);
    if (this.button) this.button.disabled = busy;
  }

  #render() {
    const shown = this.#shown;
    const total = this.#total;
    const remaining = Math.max(total - shown, 0);

    this.style.setProperty('--progress', total ? `${Math.round((shown / total) * 100)}%` : '0%');

    if (this.label) {
      this.label.textContent = fill(this.dataset.label, { remaining });
    }

    if (this.status) {
      this.status.textContent = fill(this.dataset.progressLabel, { shown, total });
    }

    const range = this.closest('results-list')?.querySelector('.products-count-wrapper [role="status"]');
    if (range && this.dataset.rangeLabel) {
      range.textContent = fill(this.dataset.rangeLabel, { from: 1, to: shown, total });
    }

    if (this.#page >= this.#lastPage) {
      this.button?.remove();
      this.button = null;
    }
  }
}

/**
 * Replaces [name] placeholders in a label template.
 *
 * @param {string | undefined} template
 * @param {Record<string, number>} values
 * @returns {string}
 */
function fill(template, values) {
  if (!template) return '';
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`[${key}]`, String(value)),
    template
  );
}

if (!customElements.get('brand-load-more')) {
  customElements.define('brand-load-more', BrandLoadMore);
}
